import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { VehicleStatusBadge, DocExpiryBadge } from "@/components/StatusBadge";
import { ArrowLeft, Upload, Trash2, Plus, Wrench, Building2, Download, Pencil } from "lucide-react";
import { fmtDate, ORDER_CATEGORY_LABEL, ORDER_STATUS_LABEL } from "@/lib/format";
import { Field } from "./Fleet";

export default function VehicleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [docs, setDocs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("info");
  const [workshops, setWorkshops] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [equipDraft, setEquipDraft] = useState([]);

  const load = async () => {
    const v = await api.get(`/vehicles/${id}`);
    setVehicle(v.data);
    setEquipDraft(v.data.equipment || []);
    const d = await api.get(`/vehicles/${id}/documents`);
    setDocs(d.data);
    const o = await api.get(`/service-orders`, { params: { vehicle_id: id } });
    setOrders(o.data);
  };

  useEffect(() => {
    load();
    if (user.role === "rentacar_admin" || user.role === "superadmin") {
      api.get("/workshops").then((r) => setWorkshops(r.data));
    }
  }, [id]);

  const isOwner = user.role === "rentacar_admin" || user.role === "superadmin";

  const saveEquipment = async () => {
    await api.put(`/vehicles/${id}`, { equipment: equipDraft });
    load();
  };

  const addEquipment = () => setEquipDraft([...equipDraft, { name: "", checked: false, has_cert: false }]);
  const updateEquipment = (i, patch) => setEquipDraft(equipDraft.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const removeEquipment = (i) => setEquipDraft(equipDraft.filter((_, idx) => idx !== i));

  if (!vehicle) return <div className="p-8">Cargando...</div>;

  return (
    <div data-testid="vehicle-detail-page">
      <Link to="/flota" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-black mb-4">
        <ArrowLeft size={14} strokeWidth={2.5} /> Volver a flota
      </Link>

      <PageHeader
        tag={`${vehicle.year} · ${vehicle.brand}`}
        title={vehicle.plate}
        description={`${vehicle.brand} ${vehicle.model}`}
        action={
          <div className="flex items-center gap-3">
            {isOwner && (
              <button
                onClick={() => setShowEditModal(true)}
                className="h-touch px-4 border-2 border-black text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors flex items-center gap-2"
                data-testid="edit-vehicle-btn"
              >
                <Pencil size={14} strokeWidth={2.5} /> Editar
              </button>
            )}
            <VehicleStatusBadge status={vehicle.status} />
          </div>
        }
      />

      <div className="border-b border-gray-200 flex gap-1 mb-8 overflow-x-auto">
        {[
          { k: "info", l: "Información" },
          { k: "equip", l: "Equipamiento" },
          { k: "docs", l: "Documentos" },
          { k: "history", l: "Historial Taller" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-4 md:px-6 py-3 text-xs font-bold uppercase tracking-wider border-b-4 transition-colors ${
              tab === t.k ? "border-blue-600 text-black" : "border-transparent text-gray-500 hover:text-black"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="grid md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
          <InfoCell label="Patente" value={vehicle.plate} mono />
          <InfoCell label="VIN" value={vehicle.vin || "-"} mono />
          <InfoCell label="Marca" value={vehicle.brand} />
          <InfoCell label="Modelo" value={vehicle.model} />
          <InfoCell label="Año" value={vehicle.year} />
          <InfoCell label="Kilometraje" value={`${(vehicle.mileage || 0).toLocaleString("es-CL")} km`} mono />
          {isOwner && (
            <div className="bg-white p-6 md:col-span-2 flex flex-wrap gap-3">
              {!vehicle.current_workshop_id ? (
                <button onClick={() => setShowAssignModal(true)} className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 transition-colors flex items-center gap-2" data-testid="assign-workshop-btn">
                  <Wrench size={16} strokeWidth={2.5} /> Asignar a Taller
                </button>
              ) : (
                <button
                  onClick={async () => { await api.post(`/vehicles/${id}/release-workshop`); load(); }}
                  className="h-touch px-6 border-2 border-black text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
                  data-testid="release-workshop-btn"
                >
                  Liberar Taller
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "equip" && (
        <div className="border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <p className="veta-tag">Equipamiento Personalizado</p>
            {(isOwner || user.role === "workshop_admin") && (
              <button onClick={addEquipment} className="text-xs font-bold uppercase tracking-wider text-blue-600 underline" data-testid="add-equipment-btn">
                + Agregar item
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-200">
            {equipDraft.length === 0 && <p className="p-6 text-sm text-gray-500">Sin equipamiento adicional registrado.</p>}
            {equipDraft.map((eq, i) => (
              <div key={i} className="p-4 flex items-center gap-3">
                <input type="checkbox" checked={!!eq.checked} onChange={(e) => updateEquipment(i, { checked: e.target.checked })} className="w-5 h-5" data-testid={`equip-check-${i}`} />
                <input
                  value={eq.name}
                  placeholder="Ej: Pértiga de seguridad"
                  onChange={(e) => updateEquipment(i, { name: e.target.value })}
                  className="flex-1 h-12 px-3 border border-gray-200"
                  data-testid={`equip-name-${i}`}
                />
                {isOwner && (
                  <button onClick={() => removeEquipment(i)} className="p-2 text-gray-500 hover:text-red-600">
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {(isOwner || user.role === "workshop_admin") && equipDraft.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <button onClick={saveEquipment} className="h-touch px-8 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600" data-testid="save-equipment-btn">
                Guardar Equipamiento
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "docs" && (
        <div>
          <div className="flex justify-end mb-4">
            {isOwner && (
              <button onClick={() => setShowDocModal(true)} className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-2" data-testid="new-document-btn">
                <Upload size={16} strokeWidth={2.5} /> Subir Documento
              </button>
            )}
          </div>
          <div className="border border-gray-200">
            {docs.length === 0 && <p className="p-6 text-sm text-gray-500">Sin documentos registrados.</p>}
            <div className="divide-y divide-gray-200">
              {docs.map((d) => (
                <div key={d.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`doc-row-${d.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-bold text-sm text-black">{d.doc_type}</p>
                    <p className="text-xs text-gray-500 font-mono">{d.original_filename}</p>
                    <p className="text-xs text-gray-500">Vence: {fmtDate(d.expiry_date)}</p>
                  </div>
                  <DocExpiryBadge expiryDate={d.expiry_date} />
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files?path=${encodeURIComponent(d.file_path)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold uppercase tracking-wider text-blue-600 underline flex items-center gap-1"
                  >
                    <Download size={14} strokeWidth={2.5} /> Descargar
                  </a>
                  {isOwner && (
                    <button onClick={async () => { await api.delete(`/documents/${d.id}`); load(); }} className="text-red-600 hover:bg-red-50 p-2">
                      <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="border border-gray-200">
          {orders.length === 0 && <p className="p-6 text-sm text-gray-500">Sin órdenes de servicio registradas.</p>}
          <div className="divide-y divide-gray-200">
            {orders.map((o) => (
              <Link key={o.id} to={`/taller/orden/${o.id}`} className="block p-4 hover:bg-gray-50" data-testid={`order-row-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-bold text-sm">{ORDER_CATEGORY_LABEL[o.category]}</p>
                    <p className="text-xs text-gray-500 truncate max-w-md">{o.description}</p>
                    <p className="text-xs text-gray-400 mt-1">{fmtDate(o.created_at)} · {(o.mileage || 0).toLocaleString("es-CL")} km</p>
                  </div>
                  <span className="veta-tag">{ORDER_STATUS_LABEL[o.status]}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {showAssignModal && (
        <AssignWorkshopModal
          workshops={workshops}
          vehicleId={id}
          onClose={() => setShowAssignModal(false)}
          onDone={() => { setShowAssignModal(false); load(); }}
        />
      )}

      {showDocModal && (
        <NewDocumentModal
          vehicleId={id}
          onClose={() => setShowDocModal(false)}
          onDone={() => { setShowDocModal(false); load(); }}
        />
      )}

      {showEditModal && (
        <EditVehicleModal
          vehicle={vehicle}
          onClose={() => setShowEditModal(false)}
          onDone={() => { setShowEditModal(false); load(); }}
        />
      )}
    </div>
  );
}

function InfoCell({ label, value, mono }) {
  return (
    <div className="bg-white p-6">
      <p className="veta-tag">{label}</p>
      <p className={`text-2xl font-display mt-1 tracking-tight ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function AssignWorkshopModal({ workshops, vehicleId, onClose, onDone }) {
  const [sel, setSel] = useState("");
  const submit = async () => {
    if (!sel) return;
    await api.post(`/vehicles/${vehicleId}/assign-workshop`, { workshop_company_id: sel });
    onDone();
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">Asignar a Taller</h2>
          <button onClick={onClose}>×</button>
        </div>
        <div className="p-6 space-y-4">
          <label className="veta-tag block">Taller</label>
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="w-full h-touch px-3 border border-gray-300" data-testid="select-workshop">
            <option value="">Seleccione...</option>
            {workshops.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          {workshops.length === 0 && (
            <div className="status-warning p-3 text-xs">
              No hay talleres registrados. Pide a un taller que se registre en VETA CAR.
            </div>
          )}
          <button onClick={submit} disabled={!sel} className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider disabled:opacity-50" data-testid="confirm-assign-btn">
            Confirmar Asignación
          </button>
        </div>
      </div>
    </div>
  );
}

function NewDocumentModal({ vehicleId, onClose, onDone }) {
  const [docType, setDocType] = useState("Permiso de Circulación");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/vehicles/${vehicleId}/documents`, {
        doc_type: docType,
        expiry_date: expiry,
        file_path: up.data.path,
        original_filename: up.data.filename,
      });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al subir");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">Subir Documento</h2>
          <button onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5" data-testid="upload-doc-form">
          <Field label="Tipo de documento" value={docType} onChange={(e) => setDocType(e.target.value)} required testId="doc-type-input" />
          <div>
            <label className="veta-tag block mb-2">Fecha de vencimiento</label>
            <input type="date" required value={expiry} onChange={(e) => setExpiry(e.target.value)} className="w-full h-touch px-3 border border-gray-300" data-testid="doc-expiry-input" />
          </div>
          <div>
            <label className="veta-tag block mb-2">Archivo (PDF/Imagen)</label>
            <input type="file" required onChange={(e) => setFile(e.target.files?.[0])} className="w-full" accept="application/pdf,image/*" data-testid="doc-file-input" />
          </div>
          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <button type="submit" disabled={!file || loading} className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider disabled:opacity-50" data-testid="upload-doc-submit">
            {loading ? "Subiendo..." : "Subir Documento"}
          </button>
        </form>
      </div>
    </div>
  );
}


function EditVehicleModal({ vehicle, onClose, onDone }) {
  const [form, setForm] = useState({
    brand: vehicle.brand || "",
    model: vehicle.model || "",
    year: vehicle.year || new Date().getFullYear(),
    vin: vehicle.vin || "",
    mileage: vehicle.mileage || 0,
    status: vehicle.status || "available",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.put(`/vehicles/${vehicle.id}`, {
        ...form,
        year: parseInt(form.year, 10),
        mileage: parseInt(form.mileage, 10),
      });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar");
    }
    setLoading(false);
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el vehículo ${vehicle.plate}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/vehicles/${vehicle.id}`);
      window.location.href = "/flota";
    } catch (e) {
      setError(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6" data-testid="edit-vehicle-modal">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <p className="veta-tag">Edición</p>
            <h2 className="font-display text-3xl">Editar Vehículo · {vehicle.plate}</h2>
          </div>
          <button onClick={onClose} className="text-2xl font-bold">×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Marca" required value={form.brand} onChange={update("brand")} testId="edit-brand" />
            <Field label="Modelo" required value={form.model} onChange={update("model")} testId="edit-model" />
            <Field label="Año" type="number" required value={form.year} onChange={update("year")} testId="edit-year" />
            <Field label="VIN" value={form.vin} onChange={update("vin")} mono testId="edit-vin" />
            <Field label="Kilometraje" type="number" value={form.mileage} onChange={update("mileage")} testId="edit-mileage" />
            <div>
              <label className="veta-tag block mb-2">Estado</label>
              <select value={form.status} onChange={update("status")} className="w-full h-touch px-3 border border-gray-300" data-testid="edit-status">
                <option value="available">Disponible</option>
                <option value="leased">Arrendado</option>
                <option value="in_workshop">En Taller</option>
                <option value="out_of_service">Fuera de Servicio</option>
              </select>
            </div>
          </div>
          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={loading} className="flex-1 h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60 active:scale-[0.98] transition-colors" data-testid="save-edit-vehicle">
              {loading ? "Guardando..." : "Guardar Cambios"}
            </button>
            <button type="button" onClick={remove} className="h-touch px-6 border-2 border-red-600 text-red-600 text-sm font-bold uppercase tracking-wider hover:bg-red-600 hover:text-white transition-colors" data-testid="delete-vehicle">
              Eliminar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
